import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TableAction, TableColumn } from '../shared/models/table-models';
import { Table } from '../shared/table/table';
import { Popup } from '../../core/shared/components/popup/popup';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ClientUI, ClientResponse, ClientExposureResponse } from '../setup/shared/models/clientui';
import { DynamicFieldConfig } from '../../core/shared/dynamic-form/form-config.model';
import { Setup } from '../setup/shared/service/setup';
import { ToastService } from '../../core/shared/services/toast.service';
import { PickListService } from '../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../app/core/shared/models/picklist.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  NgApexchartsModule,
  ApexNonAxisChartSeries,
  ApexChart,
  ApexLegend,
  ApexDataLabels,
  ApexStroke,
  ApexTooltip,
  ApexPlotOptions,
  ApexStates,
} from 'ng-apexcharts';

// Shape consumed by the [chart] input on <apx-chart> for a donut/pie chart.
export type SummaryPieChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  colors: string[];
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  plotOptions: ApexPlotOptions;
  states: ApexStates;
};

@Component({
  selector: 'app-client',
  imports: [Table, Popup, CommonModule, FormsModule, TranslatePipe, NgApexchartsModule],
  templateUrl: './client.html',
  styleUrl: './client.css',
})
export class Client implements OnInit {
  private translate = inject(TranslateService);
  selectedClient: ClientUI | null = null;
  facilityTypeMap: Record<string, string> = {};
  facilityTypeKeys: Record<string, number> = {};
  showPopup = false;
  faPencil = faPencil;
  faTrash = faTrash;
  popupTitle = '';
  popupType: 'form' | 'confirm' = 'form'; // ✅ controls popup mode
  popupMessage = '';
  popupFields: DynamicFieldConfig[] = [];
  data: ClientUI[] = [];
  totalConventional = 0;
  totalIslamic = 0;
  totalNpl = 0;
  GrandTotal = 0;

  // ================= SUMMARY PIE CHART =================.
  readonly summaryChartColors = ['#6366F1', '#14B8A6', '#F43F5E']; // Conventional, Islamic, NPL
  summaryChartOptions: SummaryPieChartOptions = this.buildSummaryChartOptions(0, 0, 0);
  columns: TableColumn[] = [
    { key: 'name', label: 'CLIENT.NAME' },
    { key: 'conventional', label: 'CLIENT.CONVENTIONAL', type: 'currency' },
    { key: 'islamic', label: 'CLIENT.ISLAMIC', type: 'currency' },
    { key: 'npl', label: 'CLIENT.NPL', type: 'currency' },
    { key: 'totalExpsr', label: 'CLIENT.TOTAL_EXPOSURE', type: 'currency' },
    { key: 'totalMrkUp', label: 'CLIENT.TOTAL_MARKUP', type: 'currency' }
  ];
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: ClientUI) => this.openEditPopup(row),
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: ClientUI) => this.openDeletePopup(row),
    },
  ];

  constructor(
    private clientService: Setup,
    private toast: ToastService,
    private pickListService: PickListService,
  ) {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.summaryChartOptions = this.buildSummaryChartOptions(
        this.totalConventional,
        this.totalIslamic,
        this.totalNpl,
      );
    });
  }

  ngOnInit(): void {
    this.loadFacilityTypes();
  }

  /* ================= LOAD ================= */
  loadClients(): void {
    this.clientService.getclientList().subscribe(
      (res: ClientResponse[]) => {
        this.data = res.map((item: ClientResponse) => {
          const exposures: ClientExposureResponse[] = item.exposures ?? [];
          const conventional =
            exposures.find(
              exposure =>
                exposure.fcltyTypKey === this.facilityTypeKeys['conventional'],
            )?.totalExposure ?? null;
          const islamic =
            exposures.find(
              exposure =>
                exposure.fcltyTypKey === this.facilityTypeKeys['islamic'],
            )?.totalExposure ?? null;
          const npl =
            exposures.find(
              exposure =>
                exposure.fcltyTypKey === this.facilityTypeKeys['npl'],
            )?.totalExposure ?? null;
          return {
            clientSeq: item.clientSeq,
            name: item.clntNm.trim(),
            conventional,
            islamic,
            npl,
            totalExpsr: exposures.reduce(
              (sum, exposure) => sum + exposure.totalExposure,
              0,
            ),
            totalMrkUp: exposures.reduce(
              (sum, exposure) => sum + exposure.totalMarkUp,
              0,
            ),
          };
        });
        this.data.sort((a, b) => (b.totalExpsr ?? 0) - (a.totalExpsr ?? 0));
        this.totalConventional = this.data.reduce(
          (sum, item) => sum + (item.conventional ?? 0),
          0,
        );
        this.totalIslamic = this.data.reduce(
          (sum, item) => sum + (item.islamic ?? 0),
          0,
        );
        this.totalNpl = this.data.reduce(
          (sum, item) => sum + (item.npl ?? 0),
          0,
        );
        this.GrandTotal = this.data.reduce(
          (sum, item) =>
            sum +
            (item.conventional ?? 0) +
            (item.islamic ?? 0) +
            (item.npl ?? 0),
          0,
        );
        this.summaryChartOptions = this.buildSummaryChartOptions(
          this.totalConventional,
          this.totalIslamic,
          this.totalNpl,
        );
      },
      err => console.error(err),
    );
  }

  private buildSummaryChartOptions(
    conventional: number,
    islamic: number,
    npl: number,
  ): SummaryPieChartOptions {
    const rawValues = [conventional, islamic, npl];
    const grandTotal = conventional + islamic + npl;
    const visualValues =
      grandTotal > 0 ? this.computeVisualPieValues(rawValues) : [0, 0, 0];
    const realPercentage = (seriesIndex: number): number =>
      grandTotal > 0 ? (rawValues[seriesIndex] / grandTotal) * 100 : 0;
    return {
      series: visualValues,
      chart: {
        type: 'pie',
        width: 150,
        height: 150,
        sparkline: { enabled: true },
        dropShadow: {
          enabled: true,
          top: 2,
          left: 0,
          blur: 4,
          opacity: 0.15,
          color: '#1E293B',
        },
        animations: {
          enabled: true,
          speed: 500,
          animateGradually: { enabled: true, delay: 100 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
      },
      labels: [
        this.translate.instant('CLIENT.CONVENTIONAL'),
        this.translate.instant('CLIENT.ISLAMIC'),
        this.translate.instant('CLIENT.NPL'),
      ],
      colors: this.summaryChartColors,
      legend: { show: false },
      dataLabels: {
        enabled: true,
        formatter: (_val: number, opts: any) => `${realPercentage(opts.seriesIndex).toFixed(0)}%`,
        style: { fontSize: '11px', fontWeight: 600, fontFamily: 'inherit' },
        dropShadow: { enabled: false },
      },
      stroke: { width: 2, colors: ['#FFFFFF'] },
      states: {
        hover: { filter: { type: 'darken' } },
        active: { filter: { type: 'none' } },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
        },
      },
      tooltip: {
        y: {
          formatter: (_value: number, opts: any) =>
            `${realPercentage(opts.seriesIndex).toFixed(1)}%`,
        },
      },
    };
  }

  private computeVisualPieValues(rawValues: number[], minSharePercent = 6): number[] {
    const total = rawValues.reduce((sum, v) => sum + v, 0);
    if (total <= 0) return rawValues.map(() => 0);
    const minFraction = minSharePercent / 100;
    const shares = rawValues.map((v) => v / total);
    const boostedIdx = shares
      .map((share, i) => (rawValues[i] > 0 && share < minFraction ? i : -1))
      .filter((i) => i >= 0);
    if (boostedIdx.length === 0) return [...rawValues];
    const unboostedIdx = rawValues.map((_, i) => i).filter((i) => !boostedIdx.includes(i));
    const sumUnboosted = unboostedIdx.reduce((sum, i) => sum + rawValues[i], 0);
    const rankedBoosted = [...boostedIdx].sort((a, b) => rawValues[a] - rawValues[b]);
    const rankStep = 0.01;
    const targetShare = new Map<number, number>();
    rankedBoosted.forEach((idx, rank) => targetShare.set(idx, minFraction + rank * rankStep));
    const sumTargets = [...targetShare.values()].reduce((sum, t) => sum + t, 0);
    const safeSumTargets = Math.min(sumTargets, 0.85);
    const visual = [...rawValues];
    if (sumUnboosted > 0) {
      const scale = sumUnboosted / (1 - safeSumTargets);
      boostedIdx.forEach((idx) => {
        visual[idx] = (targetShare.get(idx) ?? minFraction) * scale;
      });
    } else {
      boostedIdx.forEach((idx) => {
        visual[idx] = targetShare.get(idx) ?? minFraction;
      });
    }
    return visual;
  }

  loadFacilityTypes(): void {
    this.pickListService
      .getOptions(PKL_GRP.FACILITY_TYPE)
      .subscribe(options => {
        this.facilityTypeKeys = {};
        options.forEach(option => {
          this.facilityTypeKeys[option.label.toLowerCase()] = Number(option.value);
        });
        this.loadClients();
      });
  }

  /* ================= POPUP OPENERS ================= */
  openAddClientPopup(): void {
    this.popupTitle = 'CLIENT.ADD_CLIENT';
    this.popupType = 'form';
    this.selectedClient = null;
    this.popupFields = [
      {
        label: 'CLIENT.CLIENT_NAME',
        key: 'name',
        type: 'text',
        value: '',
        validations: { required: true },
      },
    ];
    this.showPopup = true;
  }

  openEditPopup(row: ClientUI): void {
    this.popupTitle = 'CLIENT.EDIT_CLIENT';
    this.popupType = 'form';
    this.selectedClient = row;
    this.popupFields = [
      {
        label: 'CLIENT.CLIENT_NAME',
        key: 'name',
        type: 'text',
        value: row.name,
        validations: { required: true },
      },
    ];
    this.showPopup = true;
  }

  openDeletePopup(row: ClientUI): void {
    this.popupTitle = 'CLIENT.DELETE_CLIENT';
    this.popupType = 'confirm'; // ✅ switch popup to confirm mode
    this.selectedClient = row;
    this.popupMessage = this.translate.instant('CLIENT.CONFIRM_DELETE_MSG', { name: row.name });
    this.showPopup = true;
  }

  /* ================= SAVE / DELETE ================= */
  handleSave(formData: any): void {
    // Called only for form-type popup (Add / Edit)
    const payload = {
      clientSeq: this.selectedClient ? this.selectedClient.clientSeq : null,
      clntNm: formData.name,
      delFlg: 0,
    };

    this.clientService.saveClient(payload).subscribe({
      next: () => {
        this.toast.success('CLIENT.SAVED_SUCCESS');
        this.closePopup();
        this.loadClients();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error('CLIENT.FAILED_TO_SAVE');
      },
    });
  }

  handleDelete(): void {
    // Called only for confirm-type popup
    if (!this.selectedClient) return;

    const payload = {
      clientSeq: this.selectedClient.clientSeq,
      clntNm: this.selectedClient.name,
      delFlg: 1,
    };

    this.clientService.saveClient(payload).subscribe({
      next: () => {
        this.closePopup();
        this.loadClients();
      },
      error: (err) => {
        console.error('Delete failed', err);
        this.toast.error('CLIENT.FAILED_TO_DELETE');
      },
    });
  }

  private closePopup(): void {
    this.showPopup = false;
    this.selectedClient = null;
  }
}